import './quickStartGuide.css'

import { IonIcon } from '@ionic/react'
import {
    add,
    analyticsOutline,
    calendar,
    checkmarkSharp,
    cloudDoneOutline,
    home,
    informationCircleOutline,
    moveOutline,
    optionsOutline,
    settingsOutline,
    shareOutline,
} from 'ionicons/icons'
import React, { useEffect } from 'react'

import {
    APP_STORE_URL,
    GOOGLE_PLAY_STORE_URL,
    HOME_URL,
} from './definitions'
import FillinIcon from './FillinIcon'

import appIcon from './media/autoroster-icon-double-ticks-gradient.svg'
import appStoreBadge from './media/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg'
import googlePlayBadge from './media/GetItOnGooglePlay_Badge_Web_color_English.svg'
import netballConnectImage from './media/netball-connect.png'
import playHqImage from './media/playhq.png'
import pinImage from './media/pin.png'
import raincloudImage from './media/raincloud.png'
import sampleRosterImage from './media/sample-roster-quick-start.png'
import redCircle from './media/red-handdrawn-circle.png'
import subImage from './media/sub.png'
import {players} from './media/teamIcon'
import {FaCloudShowersHeavy, FaRandom, FaStreetView} from 'react-icons/fa'
import {PlanName} from './Plan'

const AppButton = (props: { href: string, src: string, alt: string }) => (
    <a className="quick-start-store-button" href={props.href}>
        <img src={props.src} alt={props.alt} />
    </a>
)

const FakeButton = (props: React.PropsWithChildren<{ outline?: boolean }>) => (
    <span className={`quick-start-fake-button${props.outline ? ' outline' : ''}`}>
        {props.children}
    </span>
)

const Step = (props: React.PropsWithChildren<{ number: number, title: string }>) => (
    <section className="quick-start-step">
        <div className="quick-start-step-number">{props.number}</div>
        <div>
            <h2>{props.title}</h2>
            {props.children}
        </div>
    </section>
)

const DocumentHeader = (props: { page: number, section: string }) => (
    <header className="quick-start-document-header">
        <div className="quick-start-brand">
            <img src={appIcon} alt="" />
            <div>
                <strong>AutoRoster</strong>
                <span>Quick Start Guide for Club Coaches</span>
            </div>
        </div>
        <div className="quick-start-page-label">
            <span>{props.section}</span>
            <strong>{props.page} / 2</strong>
        </div>
    </header>
)

const PageFooter = () => (
    <footer className="quick-start-page-footer">
        <span>AutoRoster for Netball</span>
        <a href={HOME_URL}>autoroster.io</a>
        <span>Open the <HelpTab /> tab for full instructions</span>
    </footer>
)

const HelpTab = () => (
    <span className="quick-start-help-tab">
        <IonIcon icon={informationCircleOutline} />
        Help
    </span>
)

const FirstPage = () => (
    <article className="quick-start-sheet">
        <DocumentHeader page={1} section="SETUP &amp; FIRST ROSTER" />

        <div className="quick-start-title-row">
            <div>
                <p className="quick-start-eyebrow">WELCOME, COACH!</p>
                <h1>From club invite to first roster</h1>
                <p className="quick-start-lede">
                    Follow these steps to join your club, set up your team, and make a useful roster.
                </p>
            </div>
            <div className="quick-start-install">
                <strong>Get the app!</strong>
                <div>
                    <AppButton href={APP_STORE_URL} src={appStoreBadge} alt="Download on the App Store" />
                    <AppButton href={GOOGLE_PLAY_STORE_URL} src={googlePlayBadge} alt="Get it on Google Play" />
                </div>
                <a href={HOME_URL}>or use AutoRoster in your browser</a>
            </div>
        </div>

        <div className="quick-start-setup-grid">
            <Step number={1} title="Open your club invitation">
                <p>
                    Tap the link sent by your club, then
                    choose <FakeButton>Create account</FakeButton> or <FakeButton outline>Sign in</FakeButton>
                </p>
                <p>
                    Once signed in, tap <FakeButton>Join &lt;Club&gt;</FakeButton> and confirm.
                </p>
            </Step>

            <Step number={2} title="Find your team">
                <p>
                    To create a brand new team, tap <FakeButton><IonIcon icon={add} /> Add Team</FakeButton>
                </p>
                <p>
                    Or if your club wants you to access an existing team on AutoRoster, ask a club admin to add you to it now.
                    When they're done, it'll appear in your Team Selector (<span className="quick-start-icon-label"><IonIcon icon={home} /> Home</span>).
                </p>
            </Step>

            <Step number={3} title="Add a season">
                <p>
                    Tap <FakeButton><IonIcon icon={add} /> Add Season</FakeButton>,
                    enter a name, check settings,
                    then <FakeButton><IonIcon icon={checkmarkSharp} /> Create Season</FakeButton>
                </p>
                <p>
                    Teams can have multiple seasons* (e.g. "Spring 2026", "Tournament").
                    From <span className="quick-start-icon-label"><IonIcon icon={calendar} /> Games</span>:
                    {' '}
                    <FakeButton outline><IonIcon icon={settingsOutline} /> Season settings</FakeButton>
                    {' '}
                    &gt;
                    {' '}
                    <FakeButton outline>+ Add New Season for...</FakeButton>
                </p>
            </Step>

            <Step number={4} title="Add players and games">
                <p>
                    In <span className="quick-start-icon-label"><IonIcon icon={players} /> Players</span>, tap <FakeButton><IonIcon icon={add} /> Add Player</FakeButton> and repeat as necessary.
                </p>
                <p>
                    Games can often be imported automatically &mdash; see <b>Fixture Sync</b> below. Otherwise
                    in <span className="quick-start-icon-label"><IonIcon icon={calendar} /> Games</span> use <FakeButton><IonIcon icon={add} /> Add Game</FakeButton>.
                </p>
            </Step>
        </div>

        <section className="quick-start-sync-panel">
            <div className="quick-start-sync-heading">
                <IonIcon icon={cloudDoneOutline} />
                <div>
                    <h2 className="quick-start-save-time">Save time with Fixture Sync</h2>
                    <p>Auto-syncs your games and scores (subject to online availability).</p>
                </div>
            </div>
            <div className="quick-start-provider-logos">
                <img src={netballConnectImage} alt="NetballConnect" />
                <img src={playHqImage} alt="PlayHQ / NetballHQ" />
            </div>
            <ul>
                <li>When creating a season: <b>Fixture Sync: <FakeButton outline>Connect…</FakeButton></b></li>
                <li>Or set it up later: <span className="quick-start-icon-label"><IonIcon icon={calendar} /> Games</span> &gt; <FakeButton outline><IonIcon icon={settingsOutline} /> Season settings</FakeButton>
                </li>
                <li>Available when your league uses NetballConnect or PlayHQ / NetballHQ</li>
            </ul>
        </section>

        <section className="quick-start-first-roster">
            <div className="quick-start-section-title">
                <span>YOUR FIRST ROSTER</span>
                <h2>Set preferences, hit <FakeButton><FaRandom /><span>Go</span></FakeButton> and tweak to perfection</h2>
            </div>

            <div className="quick-start-roster-content">
                <div className="quick-start-roster-instructions">
                    <div className="quick-start-roster-entry">
                        <div><IonIcon icon={calendar} /><span><b>Open a game</b><small>Games → tap game</small></span></div>
                        <i>›</i>
                        <div><span><b>Enter Edit mode</b><small>Tap <FakeButton>Edit</FakeButton></small></span></div>
                    </div>

                    <div className="quick-start-roster-steps-grid">
                        <section>
                            <div className="quick-start-roster-step-heading">
                                <span className="sh-num">1</span><span className="sh-wrap"><FaStreetView /></span><h3>Position Preferences <span className="aside">(optional)</span></h3>
                            </div>
                            <p>
                                Nominate positions a player should or shouldn't receive.
                            </p>
                        </section>

                        <section>
                            <div className="quick-start-roster-step-heading">
                                <span className="sh-num">2</span><span className="sh-wrap"><IonIcon icon={optionsOutline} /></span><h3>Roster Settings</h3>
                            </div>
                            <p>Choose a preset, or customise your priorities.</p>
                        </section>

                        <section className="quick-start-go-step">
                            <div className="quick-start-roster-step-heading">
                                <span className="sh-num">3</span><span className="sh-wrap"><FaRandom /></span><h3>Go</h3>
                            </div>
                            <p>
                                Tap <FakeButton><FaRandom /> Go</FakeButton> to
                                auto-generate a roster.
                            </p>
                            <p>
                                Don't like it? Tweak your Position Preferences and
                                Roster Settings.
                            </p>
                        </section>

                        <section className="quick-start-drag-step">
                            <div className="quick-start-roster-step-heading">
                                <span className="sh-num">4</span><IonIcon icon={moveOutline} /><h3>Drag</h3>
                            </div>
                            <p>
                                Drag positions, players, or entire periods exactly where you want them. Create subs with long-press.
                            </p>
                        </section>
                    </div>
                </div>

                <img
                    className="quick-start-roster-screenshot"
                    src={sampleRosterImage}
                    alt="Example AutoRoster roster in Edit mode, with Settings, Position Preferences and Go controls"
                />
                <img className="quick-start-red-circle quick-start-red-circle-1" src={redCircle} alt="" />
                <img className="quick-start-red-circle quick-start-red-circle-2" src={redCircle} alt="" />
                <img className="quick-start-red-circle quick-start-red-circle-3" src={redCircle} alt="" />
            </div>
        </section>

        <PageFooter />
    </article>
)

const TipCard = (props: React.PropsWithChildren<{
    title: string,
    kicker: string,
    image?: string,
    imageAlt?: string,
    className?: string,
}>) => (
    <section className={`quick-start-tip-card ${props.className || ''}`}>
        <div className="quick-start-tip-heading">
            <div>
                <span>{props.kicker}</span>
                <h2>{props.title}</h2>
            </div>
            {props.image ? <img src={props.image} alt={props.imageAlt || ''} /> : null}
        </div>
        {props.children}
    </section>
)

const SecondPage = () => (
    <article className="quick-start-sheet">
        <DocumentHeader page={2} section="COURTSIDE SHORTCUTS" />

        <div className="quick-start-title-row second-page-title">
            <div>
                <h1>Useful tricks &amp; quick fixes</h1>
                <p className="quick-start-lede">
                    Find more in the <HelpTab /> tab &rarr; Tips &amp; Tricks.
                </p>
            </div>
            <div className="quick-start-help-chip">
                Full details are always in the<br />AutoRoster <HelpTab /> tab
            </div>
        </div>

        <div className="quick-start-tips-grid">
            <TipCard kicker="MID-PERIOD CHANGE" title="Substitutions" image={subImage} imageAlt="A split-position substitution">
                <p>Give a player two positions in one period:</p>
                <ol>
                    <li>In Edit mode, <b>press and hold</b> a position until it lifts.</li>
                    <li>Drag the floating half to a new location and release.</li>
                </ol>
                <p className="quick-start-small">Undo by dragging the second half back again.</p>
            </TipCard>

            <TipCard kicker="LOCK IN A CHOICE" title="Pin" image={pinImage} imageAlt="A pinned roster position">
                <p>
                    Tap a roster position without dragging. The pin means it will stay where it is when you
                    tap <span className="quick-start-fake-button"><FaRandom />Go</span>.
                </p>
                <p className="quick-start-small">Tap again to remove the pin.</p>
            </TipCard>

            <TipCard kicker="KEEP STATS HONEST" title="Raincloud" image={raincloudImage} imageAlt="A raincloud-marked roster position">
                <p>
                    After a game, if a position was not actually played &mdash; e.g. because of rain or injury &mdash; tap
                    until <FaCloudShowersHeavy className="icon-raincloud" /> appears.
                </p>
                <p className="quick-start-small">Raincloud roles are excluded from player and team statistics.</p>
            </TipCard>

            <TipCard kicker="TEMPORARY PLAYER" title="Fill-ins" className="fillin-tip">
                <div className="quick-start-fillin-icon"><FillinIcon /></div>
                <p>
                    When creating or editing a player, tick <b>This player is a fill-in</b>. They remain available for
                    rosters but don't clutter team lists.
                </p>
            </TipCard>
        </div>

        <section className="quick-start-matchday">
            <div className="quick-start-section-title">
                <span>STATS</span>
                <h2>Let AutoRoster analyze your team</h2>
            </div>
            <div className="quick-start-matchday-grid">
                <div>
                    <b>NetStats</b>
                    <ul>
                        <li>Finds correlations between team performance and player positions.</li>
                        <li>Grows more accurate over time.</li>
                    </ul>
                </div>
                <div>
                    <b>Combinations</b>
                    <ul>
                        <li>Reveals which players work well together &amp; which don't</li>
                        <li>Identifies pairs and trios</li>
                    </ul>
                </div>
                <div>
                    <b>Positions Played</b>
                    <ul>
                        <li>Tracks who's played where &amp; how often</li>
                        <li>Helps avoid over-benching players</li>
                    </ul>
                </div>
            </div>
            <div className="quick-start-matchday-text">
                <IonIcon icon={analyticsOutline} />
                <div>
                    <p>
                        NetStats and Combinations need period-by-period scores for your games. Supply these
                        by enabling <b>Fixture Sync</b> or by entering scores manually from <b><IonIcon icon={calendar} /> Games</b> &rarr;
                        tap “<b>...</b>” on any game &rarr; <b>Edit game</b>.
                    </p>
                    <p className="quick-start-small">
                        NetStats (including those shown on rosters) are visible only to team coaches and club admins, even when the roster link is shared.
                    </p>
                </div>
            </div>
        </section>

        <section className="quick-start-troubleshooting">
            <div className="quick-start-trouble-heading">
                <div className="quick-start-warning">!</div>
                <div>
                    <span>TROUBLESHOOTING</span>
                    <h2>“You can’t edit this roster”</h2>
                </div>
            </div>
            <p>Anyone can view a shared roster, but only a coach assigned to that team can edit it.</p>
            <div className="quick-start-check-grid">
                <div><b>1</b><span>Check that you're signed in.</span></div>
                <div><b>2</b><span>Use the same sign-in method as when you created your account.</span></div>
                <div><b>3</b><span>Ask your club admin to assign you as coach of this team.</span></div>
            </div>
            <div className="quick-start-trouble-footer">
                <span>Still stuck? <HelpTab /> &rarr; <FakeButton outline>Email Max</FakeButton></span>
                <span><IonIcon icon={shareOutline} /> Sharing a roster does not grant editing access.</span>
            </div>
        </section>

        <section className="quick-start-bottom-notes">
            <div>
                <IonIcon icon={cloudDoneOutline} />
                <span>
                    <b>
                        Changes are saved automatically.
                    </b> Your roster is stored in the cloud and available from any device.
                </span>
            </div>
            <div>
                <span className="quick-start-asterisk">*</span>
                <span>
                    <PlanName /> is required for multiple teams per coach, multiple seasons per team, full Combinations, and Flow Maps.
                </span>
            </div>
        </section>
        <PageFooter />
    </article>
)

const QuickStartGuide = () => {
    useEffect(() => {
        document.documentElement.classList.add('quick-start-document')

        return () => document.documentElement.classList.remove('quick-start-document')
    }, [ ])

    return (
        <main className="quick-start-preview">
            <FirstPage />
            <SecondPage />
        </main>
    )
}

export default QuickStartGuide
